import type {
  RecoveryCodesResponse,
  TotpSetupResponse,
  TwoFactorSetupComplete,
  TwoFactorStatus,
} from '@gofranz/common';
import {
  ActionIcon,
  Alert,
  Badge,
  Box,
  Button,
  Card,
  Code,
  CopyButton,
  Group,
  Loader,
  Modal,
  Paper,
  PinInput,
  Stack,
  Text,
  Title,
  Tooltip,
} from '@mantine/core';
import {
  IconAlertCircle,
  IconCheck,
  IconCopy,
  IconLock,
  IconLockOpen,
  IconShieldCheck,
  IconShieldOff,
  IconTrash,
} from '@tabler/icons-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import QRCode from 'react-qr-code';
import { useRustyState } from '../../state';

type SetupStep = 'idle' | 'qr' | 'verify' | 'recovery' | 'disable';

export interface TwoFactorSettingsProps {
  compact?: boolean;
}

export function TwoFactorSettings({ compact = false }: TwoFactorSettingsProps = {}) {
  const { t } = useTranslation();
  const { api } = useRustyState.getState();
  const [manageOpen, setManageOpen] = useState(false);

  const [status, setStatus] = useState<TwoFactorStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Setup state
  const [setupStep, setSetupStep] = useState<SetupStep>('idle');
  const [setupData, setSetupData] = useState<TotpSetupResponse | null>(null);
  const [verifyCode, setVerifyCode] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [recoveryCodes, setRecoveryCodes] = useState<string[]>([]);

  // Disable state
  const [disableCode, setDisableCode] = useState('');
  const [disabling, setDisabling] = useState(false);
  const [methodToDisable, setMethodToDisable] = useState<string | null>(null);

  // Regenerate recovery codes state
  const [regenerateModalOpen, setRegenerateModalOpen] = useState(false);
  const [regenerateCode, setRegenerateCode] = useState('');
  const [regenerating, setRegenerating] = useState(false);

  const fetchStatus = async () => {
    setLoading(true);
    setError(null);
    try {
      const twoFactorStatus = await api.getTwoFactorStatus();
      setStatus(twoFactorStatus);
    } catch (e: any) {
      console.error('Failed to fetch 2FA status:', e);
      setError(e?.response?.data?.message || e?.message || 'Failed to load 2FA status');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStatus();
  }, []);

  const handleStartSetup = async () => {
    setError(null);
    try {
      const data = await api.setupTotp();
      setSetupData(data);
      setSetupStep('qr');
    } catch (e: any) {
      console.error('Failed to start TOTP setup:', e);
      setError(e?.response?.data?.message || e?.message || 'Failed to start setup');
    }
  };

  const handleVerifySetup = async () => {
    if (verifyCode.length < 6 || !setupData) return;

    setVerifying(true);
    setError(null);
    try {
      const result: TwoFactorSetupComplete = await api.verifyTotpSetup(
        setupData.setup_token,
        verifyCode
      );
      setRecoveryCodes(result.recovery_codes);
      setSetupStep('recovery');
      // Refresh status
      await fetchStatus();
    } catch (e: any) {
      console.error('Failed to verify TOTP:', e);
      setError(e?.response?.data?.message || e?.message || 'Invalid code. Please try again.');
      setVerifyCode('');
    } finally {
      setVerifying(false);
    }
  };

  const handleDisable = async () => {
    if (!methodToDisable || disableCode.length < 6) return;

    setDisabling(true);
    setError(null);
    try {
      await api.disableTwoFactorMethod(methodToDisable, disableCode);
      setSetupStep('idle');
      setMethodToDisable(null);
      setDisableCode('');
      await fetchStatus();
    } catch (e: any) {
      console.error('Failed to disable 2FA:', e);
      setError(e?.response?.data?.message || e?.message || 'Failed to disable. Check your code.');
    } finally {
      setDisabling(false);
    }
  };

  const handleRegenerateRecoveryCodes = async () => {
    if (regenerateCode.length < 6) return;

    setRegenerating(true);
    setError(null);
    try {
      const result: RecoveryCodesResponse = await api.regenerateRecoveryCodes(regenerateCode);
      setRecoveryCodes(result.codes);
      setSetupStep('recovery');
      setRegenerateModalOpen(false);
      setRegenerateCode('');
      await fetchStatus();
    } catch (e: any) {
      console.error('Failed to regenerate recovery codes:', e);
      setError(
        e?.response?.data?.message || e?.message || 'Failed to regenerate. Check your code.'
      );
    } finally {
      setRegenerating(false);
    }
  };

  const handleCloseSetup = () => {
    setSetupStep('idle');
    setSetupData(null);
    setVerifyCode('');
    setRecoveryCodes([]);
    setError(null);
  };

  const handleCloseDisable = () => {
    setSetupStep('idle');
    setMethodToDisable(null);
    setDisableCode('');
    setError(null);
  };

  if (loading) {
    if (compact) {
      return (
        <Group justify="center" p="md">
          <Loader size="sm" />
          <Text size="sm">{t('twoFactor.loading')}</Text>
        </Group>
      );
    }
    return (
      <Card withBorder p="md">
        <Group justify="center" p="xl">
          <Loader size="sm" />
          <Text>{t('twoFactor.loading')}</Text>
        </Group>
      </Card>
    );
  }

  const ManagedMethodsBody = () => (
    <Stack gap="sm">
      {status?.methods.map((method) => (
        <Paper key={method.id} withBorder p="sm">
          <Group justify="space-between">
            <Group>
              <IconShieldCheck size={20} color="green" />
              <Box>
                <Text size="sm" fw={500}>
                  {method.name}
                </Text>
                <Text size="xs" c="dimmed">
                  {method.method_type.toUpperCase()}
                </Text>
              </Box>
            </Group>
            <ActionIcon
              color="red"
              variant="subtle"
              onClick={() => {
                setMethodToDisable(method.id);
                setSetupStep('disable');
                setManageOpen(false);
              }}
            >
              <IconTrash size={18} />
            </ActionIcon>
          </Group>
        </Paper>
      ))}

      <Group mt="sm">
        <Text size="sm" c="dimmed">
          {t('twoFactor.recoveryCodesRemaining')}: {status?.recovery_codes_remaining}
        </Text>
        <Button
          size="xs"
          variant="subtle"
          onClick={() => {
            setRegenerateModalOpen(true);
            setManageOpen(false);
          }}
        >
          {t('twoFactor.regenerateRecoveryCodes')}
        </Button>
      </Group>
    </Stack>
  );

  return (
    <>
      {compact ? (
        <>
          {error && (
            <Alert icon={<IconAlertCircle size={16} />} color="red" mb="sm">
              {error}
            </Alert>
          )}
          <Group
            justify="space-between"
            p="md"
            style={{
              border: '1px solid var(--mantine-color-default-border)',
              borderRadius: 'var(--mantine-radius-md)',
            }}
          >
            <Group gap="md">
              <Box
                style={{
                  width: 40,
                  height: 40,
                  borderRadius: 999,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: status?.enabled
                    ? 'var(--mantine-color-green-light)'
                    : 'var(--mantine-color-red-light)',
                  color: status?.enabled
                    ? 'var(--mantine-color-green-7)'
                    : 'var(--mantine-color-red-7)',
                }}
              >
                {status?.enabled ? <IconLock size={20} /> : <IconLockOpen size={20} />}
              </Box>
              <Box>
                <Text fw={500} size="sm">
                  {t('login.twoFactorAuth')}
                </Text>
                <Text size="xs" fw={700} tt="uppercase" c={status?.enabled ? 'green.7' : 'red.7'}>
                  {status?.enabled ? t('twoFactor.enabled') : t('twoFactor.disabled')}
                </Text>
              </Box>
            </Group>
            <Button
              variant="subtle"
              size="sm"
              onClick={() => {
                if (status?.enabled) {
                  setManageOpen(true);
                } else {
                  handleStartSetup();
                }
              }}
            >
              {status?.enabled ? t('twoFactor.regenerateRecoveryCodes') : t('twoFactor.enable')}
            </Button>
          </Group>

          <Modal
            opened={manageOpen}
            onClose={() => setManageOpen(false)}
            title={t('login.twoFactorAuth')}
            size="md"
          >
            <ManagedMethodsBody />
          </Modal>
        </>
      ) : (
        <Card withBorder p="md">
          <Group justify="space-between" mb="md">
            <Group>
              {status?.enabled ? (
                <IconShieldCheck size={24} color="green" />
              ) : (
                <IconShieldOff size={24} color="gray" />
              )}
              <Title order={4}>{t('login.twoFactorAuth')}</Title>
            </Group>
            <Badge color={status?.enabled ? 'green' : 'gray'}>
              {status?.enabled ? t('twoFactor.enabled') : t('twoFactor.disabled')}
            </Badge>
          </Group>

          {error && (
            <Alert icon={<IconAlertCircle size={16} />} color="red" mb="md">
              {error}
            </Alert>
          )}

          <Text size="sm" c="dimmed" mb="md">
            {t('twoFactor.description')}
          </Text>

          {status?.enabled && status.methods.length > 0 ? (
            <ManagedMethodsBody />
          ) : (
            <Button onClick={handleStartSetup} leftSection={<IconShieldCheck size={18} />}>
              {t('twoFactor.enable')}
            </Button>
          )}
        </Card>
      )}

      {/* Setup Modal - QR Code Step */}
      <Modal
        opened={setupStep === 'qr'}
        onClose={handleCloseSetup}
        title={t('twoFactor.setupTitle')}
        size="md"
      >
        <Stack gap="md" align="center">
          <Text size="sm" c="dimmed" ta="center">
            {t('twoFactor.scanQRCode')}
          </Text>

          {setupData && (
            <>
              <Paper withBorder p="md" bg="white">
                <QRCode value={setupData.otpauth_uri} size={200} />
              </Paper>

              <Text size="xs" c="dimmed" ta="center">
                {t('twoFactor.cantScanQR')}
              </Text>

              <Group gap="xs">
                <Code>{setupData.secret}</Code>
                <CopyButton value={setupData.secret}>
                  {({ copied, copy }) => (
                    <Tooltip label={copied ? t('common.copied') : t('common.copy')}>
                      <ActionIcon variant="subtle" onClick={copy}>
                        {copied ? <IconCheck size={16} /> : <IconCopy size={16} />}
                      </ActionIcon>
                    </Tooltip>
                  )}
                </CopyButton>
              </Group>
            </>
          )}

          <Button onClick={() => setSetupStep('verify')} fullWidth>
            {t('twoFactor.next')}
          </Button>
        </Stack>
      </Modal>

      {/* Setup Modal - Verify Step */}
      <Modal
        opened={setupStep === 'verify'}
        onClose={handleCloseSetup}
        title={t('twoFactor.verifySetup')}
        size="sm"
      >
        <Stack gap="md" align="center">
          <Text size="sm" c="dimmed" ta="center">
            {t('twoFactor.enterCodeFromApp')}
          </Text>

          {error && (
            <Alert icon={<IconAlertCircle size={16} />} color="red" w="100%">
              {error}
            </Alert>
          )}

          <PinInput
            length={6}
            type="number"
            value={verifyCode}
            onChange={setVerifyCode}
            oneTimeCode
            disabled={verifying}
            size="lg"
          />

          <Button
            onClick={handleVerifySetup}
            loading={verifying}
            disabled={verifyCode.length < 6}
            fullWidth
          >
            {t('login.verify')}
          </Button>

          <Button variant="subtle" onClick={() => setSetupStep('qr')}>
            {t('login.back')}
          </Button>
        </Stack>
      </Modal>

      {/* Setup Modal - Recovery Codes Step */}
      <Modal
        opened={setupStep === 'recovery'}
        onClose={handleCloseSetup}
        title={t('twoFactor.recoveryCodes')}
        size="md"
      >
        <Stack gap="md">
          <Alert icon={<IconAlertCircle size={16} />} color="yellow">
            {t('twoFactor.saveRecoveryCodes')}
          </Alert>

          <Paper withBorder p="md">
            <Stack gap="xs">
              {recoveryCodes.map((code, index) => (
                <Group key={index} justify="space-between">
                  <Code>{code}</Code>
                </Group>
              ))}
            </Stack>
          </Paper>

          <CopyButton value={recoveryCodes.join('\n')}>
            {({ copied, copy }) => (
              <Button
                variant="light"
                leftSection={copied ? <IconCheck size={16} /> : <IconCopy size={16} />}
                onClick={copy}
              >
                {copied ? t('common.copied') : t('twoFactor.copyAllCodes')}
              </Button>
            )}
          </CopyButton>

          <Button onClick={handleCloseSetup}>{t('common.done')}</Button>
        </Stack>
      </Modal>

      {/* Disable 2FA Modal */}
      <Modal
        opened={setupStep === 'disable'}
        onClose={handleCloseDisable}
        title={t('twoFactor.disable2FA')}
        size="sm"
      >
        <Stack gap="md" align="center">
          <Alert icon={<IconAlertCircle size={16} />} color="red" w="100%">
            {t('twoFactor.disableWarning')}
          </Alert>

          <Text size="sm" c="dimmed" ta="center">
            {t('twoFactor.enterCodeToDisable')}
          </Text>

          {error && (
            <Alert icon={<IconAlertCircle size={16} />} color="red" w="100%">
              {error}
            </Alert>
          )}

          <PinInput
            length={6}
            type="number"
            value={disableCode}
            onChange={setDisableCode}
            oneTimeCode
            disabled={disabling}
            size="lg"
          />

          <Group w="100%">
            <Button variant="subtle" onClick={handleCloseDisable} flex={1}>
              {t('common.cancel')}
            </Button>
            <Button
              color="red"
              onClick={handleDisable}
              loading={disabling}
              disabled={disableCode.length < 6}
              flex={1}
            >
              {t('twoFactor.disable')}
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* Regenerate Recovery Codes Modal */}
      <Modal
        opened={regenerateModalOpen}
        onClose={() => {
          setRegenerateModalOpen(false);
          setRegenerateCode('');
          setError(null);
        }}
        title={t('twoFactor.regenerateRecoveryCodes')}
        size="sm"
      >
        <Stack gap="md" align="center">
          <Alert icon={<IconAlertCircle size={16} />} color="yellow" w="100%">
            {t('twoFactor.regenerateWarning')}
          </Alert>

          <Text size="sm" c="dimmed" ta="center">
            {t('twoFactor.enterCodeToRegenerate')}
          </Text>

          {error && (
            <Alert icon={<IconAlertCircle size={16} />} color="red" w="100%">
              {error}
            </Alert>
          )}

          <PinInput
            length={6}
            type="number"
            value={regenerateCode}
            onChange={setRegenerateCode}
            oneTimeCode
            disabled={regenerating}
            size="lg"
          />

          <Group w="100%">
            <Button
              variant="subtle"
              onClick={() => {
                setRegenerateModalOpen(false);
                setRegenerateCode('');
              }}
              flex={1}
            >
              {t('common.cancel')}
            </Button>
            <Button
              onClick={handleRegenerateRecoveryCodes}
              loading={regenerating}
              disabled={regenerateCode.length < 6}
              flex={1}
            >
              {t('twoFactor.regenerate')}
            </Button>
          </Group>
        </Stack>
      </Modal>
    </>
  );
}
